import { useEffect, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';

/** React-level guard: a pointer down inside interactive content never starts a canvas gesture. */
export const stopCanvasPointer = (e: ReactPointerEvent<HTMLElement>): void => {
  e.stopPropagation();
};

/**
 * Wheel events are native on the canvas root, so isolation must be native too: when
 * the content under the pointer can scroll, the event stops here instead of panning
 * the world. Zoom gestures (ctrl/⌘+wheel) always reach the canvas.
 */
export const useIsolateWheel = (ref: RefObject<HTMLElement | null>): void => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) return;
      const target = e.target instanceof HTMLElement ? e.target : el;
      const scroller = target.closest('[data-scrollable]') ?? el;
      if (scroller instanceof HTMLElement && scroller.scrollHeight > scroller.clientHeight + 1) e.stopPropagation();
    };
    el.addEventListener('wheel', onWheel, { passive: true });
    return () => {
      el.removeEventListener('wheel', onWheel);
    };
  }, [ref]);
};
